import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, User, Mail, AtSign, Calendar, KeyRound, Users, Copy, Share2, Trophy, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { enablePush } from "@/lib/push";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0">
      <Icon className="h-4 w-4 text-green-500" />
      <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{label}</span>
      <span className="ml-auto font-semibold text-zinc-900">{value}</span>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const [enabling, setEnabling] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changing, setChanging] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => { api.get("/referral/me").then((r) => setRef(r.data)).catch(() => {}); }, []);

  const activatePush = async () => {
    setEnabling(true);
    try { await enablePush(); toast.success("Notifiche attivate! Ti avviseremo per i nuovi pronostici."); }
    catch (e) { toast.error(e.message || "Impossibile attivare le notifiche."); }
    finally { setEnabling(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("Le nuove password non coincidono."); return; }
    if (pw.new_password.length < 6) { toast.error("La nuova password deve avere almeno 6 caratteri."); return; }
    setChanging(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password aggiornata con successo.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setChanging(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(ref.link); toast.success("Link copiato!"); }
    catch { toast.error("Copia non riuscita."); }
  };
  const shareLink = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Unisciti a Il Mago Tony", text: "Iscriviti a Il Mago Tony con il mio link!", url: ref.link }); } catch {}
    } else { copyLink(); }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight text-zinc-950">Profilo</h1>
        <p className="text-sm font-semibold text-zinc-500">I tuoi dati personali (visibili solo a te).</p>
      </header>

      <div className="rounded-3xl border-2 border-zinc-950 bg-white p-6 shadow-[6px_6px_0_0_#09090b]">
        <div className="mb-4 flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-950 font-display text-2xl font-black text-green-400">
            {user.username?.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <p className="font-display text-2xl font-black uppercase text-zinc-950">{user.username}</p>
            <p className="text-sm text-zinc-500">Nickname pubblico in classifica</p>
          </div>
        </div>
        <Row icon={User} label="Nome" value={`${user.first_name} ${user.last_name}`} />
        <Row icon={Mail} label="Email" value={user.email} />
        <Row icon={AtSign} label="Nickname" value={user.username} />
        <Row icon={Calendar} label="Nato il" value={user.birth_date ? new Date(user.birth_date).toLocaleDateString("it-IT") : "-"} />
      </div>

      {/* Referral panel */}
      <div data-testid="referral-panel" className="rounded-3xl border-2 border-green-500 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-green-600" />
          <h2 className="font-display text-2xl font-extrabold uppercase text-zinc-950">Porta un amico</h2>
        </div>
        <p className="text-sm text-zinc-600">Invita i tuoi amici: per ogni iscritto tramite il tuo link guadagni <strong>5 punti</strong> nella classifica dedicata "Porta un amico".</p>

        {ref && (
          <>
            <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
              <img data-testid="referral-qr" src={ref.qr} alt="QR invito" className="h-40 w-40 rounded-2xl border border-slate-200" />
              <div className="w-full flex-1 space-y-3">
                <div>
                  <Label>Il tuo link di invito</Label>
                  <Input data-testid="referral-link" readOnly value={ref.link} className="mt-1 rounded-xl text-xs" />
                </div>
                <div className="flex gap-2">
                  <Button data-testid="referral-copy" onClick={copyLink} className="flex-1 rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">
                    <Copy className="mr-1 h-4 w-4" /> Copia
                  </Button>
                  <Button data-testid="referral-share" onClick={shareLink} variant="outline" className="flex-1 rounded-full font-bold">
                    <Share2 className="mr-1 h-4 w-4" /> Condividi
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-2xl bg-green-50 p-3 text-center">
                    <div className="font-mono-t text-2xl font-black text-green-600" data-testid="referral-count">{ref.invited_count}</div>
                    <div className="text-[10px] font-bold uppercase text-zinc-500">Amici invitati</div>
                  </div>
                  <div className="rounded-2xl bg-slate-100 p-3 text-center">
                    <div className="font-mono-t text-2xl font-black text-zinc-950" data-testid="referral-rank">{ref.rank ? `#${ref.rank}` : "-"}</div>
                    <div className="text-[10px] font-bold uppercase text-zinc-500">Posizione</div>
                  </div>
                </div>
              </div>
            </div>
            {ref.prize && (
              <div className="mt-4 flex items-center gap-2 rounded-2xl bg-zinc-950 p-3 text-white">
                <Gift className="h-5 w-5 text-green-400" />
                <span className="text-sm">Premio in palio: <strong>{ref.prize}</strong></span>
              </div>
            )}
            {ref.invited.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-500">Amici invitati</p>
                <div className="flex flex-wrap gap-2">
                  {ref.invited.map((f, i) => (
                    <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-zinc-700">
                      {f.username} · {new Date(f.created_at).toLocaleDateString("it-IT")}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-3">
          <Bell className="h-5 w-5 text-green-500" />
          <div className="flex-1">
            <p className="font-bold text-zinc-950">Notifiche push</p>
            <p className="text-sm text-zinc-500">Avviso per nuovi pronostici e scadenze imminenti.</p>
          </div>
        </div>
        <Button data-testid="enable-push-button" onClick={activatePush} disabled={enabling}
          className="mt-4 w-full rounded-full bg-green-500 font-bold text-zinc-950 hover:bg-green-600">
          {enabling ? "Attivazione..." : "Attiva notifiche"}
        </Button>
      </div>

      <form onSubmit={changePassword} className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-3">
          <KeyRound className="h-5 w-5 text-green-500" />
          <div>
            <p className="font-bold text-zinc-950">Cambia password</p>
            <p className="text-sm text-zinc-500">Aggiorna la password del tuo account in autonomia.</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Password attuale</Label>
            <Input data-testid="change-current-password" type="password" required value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-xl" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Nuova password</Label>
              <Input data-testid="change-new-password" type="password" required minLength={6} value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Conferma nuova</Label>
              <Input data-testid="change-confirm-password" type="password" required value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="rounded-xl" />
            </div>
          </div>
          <Button data-testid="change-password-button" type="submit" disabled={changing}
            className="w-full rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">
            {changing ? "Aggiornamento..." : "Aggiorna password"}
          </Button>
        </div>
      </form>
    </div>
  );
}
